import type { MouseEvent } from 'react';
import React from 'react';

import type { CrossReferenceElement, LinkElement, ParsedLink } from '@aline/common';
import { AlineTransforms, isCrossReferenceElement, parseLink } from '@aline/common';
import { IconButton, Paper, Typography } from '@mui/material';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import type { NodeEntry } from 'slate';
import { AlineTooltip, BaseSnackbar, Spacer } from 'src/components';
import { defaultEditorPopoverPaperStyles } from 'src/components/EditorPopover';
import { BookmarkIcon } from 'src/components/icons/BookmarkIcon';
import { CopyIcon } from 'src/components/icons/CopyIcon';
import { EditIcon } from 'src/components/icons/EditIcon';
import { NewWindowIcon } from 'src/components/icons/NewWindowIcon';
import { RefreshIcon } from 'src/components/icons/RefreshIcon';
import { RemoveIcon } from 'src/components/icons/RemoveIcon';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { fakeNotistackMessage, updateUrlWithoutRefresh } from 'src/helpers';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { useSetEditingLinkOpen } from './LinkPopoverContent';

const useStyles = makeStyles()(theme => ({
  clipboard: {
    display: 'flex',
  },
  icon: {
    height: theme.appSizes.iconHeight,
    width: theme.appSizes.iconWidth,
  },
  iconButton: {
    padding: theme.spacing(0.75),
  },
  link: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 14,
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
    maxWidth: 200,
    /**
     * By default flex containers don't shrink below their content width.  So, to make the link container work with no-wrap text, we set minWidth to 0
     * so the container resizes correctly.
     */
    minWidth: 0,
    textDecoration: 'none',
  },
  linkInvalid: {
    color: theme.palette.error.main,
  },
  linkText: {
    fontFamily: theme.typography.fontFamily,
    marginLeft: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  linkValid: {
    color: theme.appColors.primary,
  },
  paper: {
    ...defaultEditorPopoverPaperStyles,
    display: 'flex',
    padding: theme.spacing(1),
  },
}));

interface LinkIndicatorProps {
  linkEntry: NodeEntry<LinkElement | CrossReferenceElement>;
}

export function LinkIndicator({ linkEntry: [link, path] }: LinkIndicatorProps): JSX.Element | null {
  const isCrossReference = isCrossReferenceElement(link);
  const editor = useEditor();
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const { enqueueSnackbar } = useSnackbar();
  const { commenting, viewing } = useEditingMode();
  const { setEditingLinkOpen } = useSetEditingLinkOpen();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);

  const parsedLink = parseLink(isCrossReference ? link.href : Queries.activeLinkURL(link));
  const isBookmark = parsedLink.type === 'bookmark';
  const isInvalidUrl = parsedLink.type === 'error';

  const bookmarkText = isBookmark ? Queries.getBookmarkText(editor, parsedLink.bookmark) : null;

  // If bookmarkText returned null for a bookmark, that bookmark is invalid
  const isInvalidBookmark = isBookmark && bookmarkText === null;
  const isInvalid = isInvalidUrl || isInvalidBookmark;
  const linkDescription = getLinkDescription(parsedLink, bookmarkText);
  const Icon = parsedLink.type === 'bookmark' ? BookmarkIcon : NewWindowIcon;

  function handleClickLink(event: MouseEvent) {
    // Do not prevent opening in a new tab on macOS
    if (event.metaKey) return;

    if (parsedLink.type === 'bookmark') {
      event.preventDefault();
      const selected = AlineTransforms.selectBookmark(editor, parsedLink.bookmark);
      ClientTransforms.focus(editor);

      if (selected) {
        updateUrlWithoutRefresh(`#${encodeURIComponent(parsedLink.bookmark)}`);
      }
    }
  }

  function handleCopy() {
    copyToClipboard(parsedLink.href);

    const key = uuid4();
    enqueueSnackbar(fakeNotistackMessage, {
      content: <LinkSnack id={key} />,
      key,
    });
  }

  function handleRemove(): void {
    if (!currentUserId || !editingMode) return;

    if (isCrossReference) {
      AuthorizedTransforms.removeCrossReference(editor, {
        authorId: currentUserId,
        editingMode,
        path,
      });
    } else {
      AuthorizedTransforms.removeLink(editor, {
        authorId: currentUserId,
        editingMode,
        linkPath: path,
      });
    }

    ClientTransforms.focus(editor);
  }

  return (
    <Paper className={classes.paper}>
      <a
        className={clsx(classes.link, isInvalid ? classes.linkInvalid : classes.linkValid)}
        href={parsedLink.href}
        rel="noopener noreferrer"
        target="_blank"
        onClick={handleClickLink}
      >
        <Icon className={classes.icon} />
        <span className={classes.linkText}>{linkDescription}</span>
      </a>

      <Spacer />

      {isCrossReference && !viewing && !commenting && (
        <AlineTooltip arrow placement="bottom" title="Update reference">
          <IconButton
            className={classes.iconButton}
            color="primary"
            size="large"
            onClick={() => {
              if (!currentUserId || !editingMode) return;
              AuthorizedTransforms.updateCrossReferenceText(editor, {
                authorId: currentUserId,
                editingMode,
                path,
              });
              ClientTransforms.focus(editor);
            }}
          >
            <RefreshIcon className={classes.icon} />
          </IconButton>
        </AlineTooltip>
      )}

      <div className={classes.clipboard} data-clipboard-text={parsedLink.href}>
        <AlineTooltip arrow placement="bottom" title="Copy link">
          <IconButton
            className={classes.iconButton}
            color="primary"
            size="large"
            onClick={handleCopy}
          >
            <CopyIcon className={classes.icon} />
          </IconButton>
        </AlineTooltip>
      </div>

      {!viewing && !commenting && (
        <>
          {!isCrossReference && (
            <AlineTooltip arrow placement="bottom" title="Edit link">
              <IconButton
                className={classes.iconButton}
                color="primary"
                data-cy="link-indicator-edit-button"
                size="large"
                onClick={() => setEditingLinkOpen(path)}
              >
                <EditIcon className={classes.icon} />
              </IconButton>
            </AlineTooltip>
          )}

          <AlineTooltip
            arrow
            placement="bottom"
            title={isCrossReference ? 'Delete reference' : 'Delete link'}
          >
            <IconButton
              className={classes.iconButton}
              color="primary"
              size="large"
              onClick={handleRemove}
            >
              <RemoveIcon className={classes.icon} />
            </IconButton>
          </AlineTooltip>
        </>
      )}
    </Paper>
  );
}

interface LinkSnackProps {
  id: string;
}

function LinkSnack(props: LinkSnackProps): JSX.Element {
  return (
    <BaseSnackbar variant="success" {...props}>
      <Typography variant="caption">Link copied to clipboard</Typography>
    </BaseSnackbar>
  );
}

function getLinkDescription(parsedLink: ParsedLink, bookmarkText: string | null): string {
  switch (parsedLink.type) {
    case 'bookmark':
      if (bookmarkText === null) return 'Missing bookmark';
      if (bookmarkText.trim().length) return `Jump to '${bookmarkText}'`;
      return 'Jump to bookmark';

    case 'error':
      return 'Invalid link';

    case 'url':
      return parsedLink.href;
  }
}

function copyToClipboard(text: string) {
  return navigator.clipboard.writeText(text);
}
